import React, { useState } from "react";
import { useAuth } from "../../components/Auth/AuthProvider";
import { useTheme } from "../../App";
import { useStripe } from "../../hooks/useStripe";
import { httpsCallable } from "firebase/functions"; // ← ADD THIS
import { functions } from "../../firebase/config"; // ← ADD THIS
import styles from "./OnboardingUseCase.module.css";
import diffusionIconLight from "../../assets/diffusion_light.svg";
import diffusionIconDark from "../../assets/diffusion_dark.svg";

const creditPackages = [
  { credits: 25, price: 0, label: "25 credits/free" },
  { credits: 250, price: 9.99, label: "250 credits/$9.99" },
  { credits: 1000, price: 29.99, label: "1,000 credits/$29.99" },
  { credits: 4000, price: 79.99, label: "4,000 credits/$79.99" },
];

const OnboardingCredits: React.FC = () => {
  const { user, updateUserProfile } = useAuth();
  const { theme, setTheme } = useTheme();
  const getActualTheme = (): "light" | "dark" => {
    if (theme === "system") {
      return window.matchMedia("(prefers-color-scheme: dark)").matches
        ? "dark"
        : "light";
    }
    return theme as "light" | "dark";
  };
  const actualTheme = getActualTheme();
  const { purchaseCredits, loading: stripeLoading } = useStripe();
  const [selectedPackage, setSelectedPackage] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);

  const diffusionIcon =
    actualTheme === "dark" ? diffusionIconDark : diffusionIconLight;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedPackage === null || loading || stripeLoading) return;

    const pkg = creditPackages[selectedPackage];

    if (pkg.price === 0) {
      setLoading(true);
      try {
        await updateUserProfile({
          onboardingCompleted: true,
        });

        // ✅ ADD TO SENDGRID WHEN THEY COMPLETE ONBOARDING
        if (user) {
          try {
            const addToSendGrid = httpsCallable(functions, "addUserToSendGrid");
            const result = await addToSendGrid({
              email: user.email,
              firstName: user.displayName?.split(" ")[0] || "",
              lastName: user.displayName?.split(" ").slice(1).join(" ") || "",
            });
            // console.log("✅ SendGrid success:", result.data); // ← ADD THIS
          } catch (sendGridError) {
            // console.error("❌ SendGrid error:", sendGridError);
          }
        }

        window.history.pushState({}, "", "/new");
        window.dispatchEvent(new PopStateEvent("popstate"));
      } catch (error) {
        console.error("Error completing onboarding:", error);
        setLoading(false);
      }
      return;
    }

    if (!user) return;

    try {
      setLoading(true);
      sessionStorage.setItem("onboarding_credit_purchase", "true");
      await purchaseCredits(pkg.credits, pkg.price, user.uid);
    } catch (error) {
      console.error("Error initiating credit purchase:", error);
      setLoading(false);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <button
          className={styles.logoButton}
          onClick={() => (window.location.href = "/")}
          aria-label="Go to home"
        >
          <img src={diffusionIcon} alt="Noah" className={styles.logo} />
        </button>
      </div>

      <div className={styles.scrollableContent}>
        <div className={styles.content}>
          <h1 className={styles.title}>Which package would you like?</h1>
          <p className={styles.description}>
            You get 25 free credits, regardless of which package you choose.
          </p>

          <form onSubmit={handleSubmit} className={styles.form}>
            <div className={styles.optionsContainer}>
              {creditPackages.map((pkg, index) => (
                <button
                  key={index}
                  type="button"
                  onClick={() => setSelectedPackage(index)}
                  className={`${styles.optionButton} ${
                    selectedPackage === index ? styles.optionButtonSelected : ""
                  }`}
                >
                  {pkg.label}
                </button>
              ))}
            </div>

            <div className={styles.buttonContainer}>
              <button
                type="submit"
                disabled={selectedPackage === null || loading || stripeLoading}
                className={styles.submitButton}
              >
                {loading || stripeLoading ? "Processing..." : "Finish"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default OnboardingCredits;
